import React from 'react';
import {
  useRecentlyViewed,
  useFavorites,
  useSyte,
  useSimilarProducts,
  useTopTrunkshows,
  useDesignerAffinities,
  useLookVariants,
  Product
} from '../../hooks/useRecommendations';
import { DividerModuleProps } from '../DividerPumoModule';
import { RecommendationsCarousel } from './RecommendationsCarousel';

export const ALGORITHMS: { [key: string]: string } = {
  RECENTLY_VIEWED: 'recently viewed',
  DESIGNER_AFFINITIES: 'designer affinities',
  TOP_TRUNKSHOWS: 'top trunkshows',
  FAVORITES: 'favorites',
  SIMILAR_PRODUCTS: 'similar products',
  SYTE: 'syte',
  LOOK_VARIANTS: 'look variants'
};

interface RecommendationProps {
  divider: DividerModuleProps;
  affinityType?: string | null;
  product?: Product | undefined;
}

export const RecentlyViewed: React.FC<RecommendationProps> = ({ divider }) => {
  const { loading, error, variants } = useRecentlyViewed();
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={variants} />
  );
};

export const Favorites: React.FC<RecommendationProps> = ({ divider }) => {
  const { loading, error, variants } = useFavorites();
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={variants} />
  );
};

export const Syte: React.FC<RecommendationProps> = ({ divider, product }) => {
  const { loading, error, variants } = useSyte(product?.variantId);
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={variants} />
  );
};

export const SimilarProducts: React.FC<RecommendationProps> = ({ divider, product }) => {
  const { loading, error, variants } = useSimilarProducts(product);
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={variants} />
  );
};

export const TopTrunkshows: React.FC<RecommendationProps> = ({ divider, affinityType }) => {
  const { loading, error, trunkshows } = useTopTrunkshows(affinityType);
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={trunkshows} />
  );
};

export const DesignerAffinities: React.FC<RecommendationProps> = ({ divider, affinityType }) => {
  const { loading, error, trunkshows } = useDesignerAffinities(affinityType);
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={trunkshows} />
  );
};

export const LookVariants: React.FC<RecommendationProps> = ({ divider, product }) => {
  const { loading, error, variants } = useLookVariants(product?.variantId);
  return (
    <RecommendationsCarousel loading={loading} error={error} divider={divider} cells={variants} />
  );
};

const ALGORITHM_COMPONENT_MAP = {
  [ALGORITHMS.RECENTLY_VIEWED]: RecentlyViewed,
  [ALGORITHMS.DESIGNER_AFFINITIES]: DesignerAffinities,
  [ALGORITHMS.TOP_TRUNKSHOWS]: TopTrunkshows,
  [ALGORITHMS.FAVORITES]: Favorites,
  [ALGORITHMS.SIMILAR_PRODUCTS]: SimilarProducts,
  [ALGORITHMS.SYTE]: Syte,
  [ALGORITHMS.LOOK_VARIANTS]: LookVariants
};

interface Props {
  algorithm: string | null;
  affinityType?: string | null;
  product?: Product | undefined;
  divider: DividerModuleProps;
}

export const RecommendationsModule: React.FC<Props> = ({
  algorithm,
  affinityType,
  product,
  divider
}) => {
  if (!algorithm) return null;

  const RecommendationComponent = ALGORITHM_COMPONENT_MAP[algorithm];

  if (!RecommendationComponent) return null;

  return (
    <RecommendationComponent divider={divider} affinityType={affinityType} product={product} />
  );
};
